import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getService } from "@/data/services";
import { MaskLines, Reveal } from "@/components/site/Reveal";

const LAYOUT = [
  { slug: "new-builds", cls: "md:col-span-2 lg:col-span-7 lg:row-span-2", big: true },
  { slug: "strata", cls: "lg:col-span-5" },
  { slug: "maintenance", cls: "lg:col-span-5" },
  { slug: "domestic", cls: "lg:col-span-4" },
  { slug: "commercial", cls: "lg:col-span-4" },
  { slug: "industrial", cls: "md:col-span-2 lg:col-span-4" },
];

const Tile = ({ s, cls, big, i }) => (
  <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }} className={cls}>
    <Link to={`/services/${s.slug}`} data-testid={`bento-${s.slug}`} className="group relative block h-full min-h-[280px] overflow-hidden bg-ink clip-notch-sm">
      <img src={s.image} alt={s.title} style={{ objectPosition: s.pos }} className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06] ${big ? "opacity-100" : "opacity-90"}`} />
      <div className={`absolute inset-0 bg-gradient-to-t ${big ? "from-ink/90 via-ink/10 to-transparent" : "from-ink/95 via-ink/40 to-transparent"}`} />
      <div className="relative h-full flex flex-col justify-between p-6 sm:p-8 text-white">
        <div className="flex items-start justify-between">
          <span className="eyebrow text-white bg-ink/50 backdrop-blur px-2.5 py-1 rounded-full">{String(i + 1).padStart(2, "0")} / {s.tag}</span>
          <span className="h-11 w-11 rounded-full border border-white/30 flex items-center justify-center transition-[background-color,transform] duration-500 group-hover:bg-brass group-hover:border-brass group-hover:text-ink group-hover:rotate-45">
            <ArrowUpRight className="h-5 w-5" />
          </span>
        </div>
        <div>
          <h3 className={`font-wide font-black uppercase tracking-tight ${big ? "text-4xl sm:text-5xl lg:text-6xl" : "text-2xl sm:text-3xl"}`}>{s.title}</h3>
          <p className={`mt-3 text-slate-200 text-sm leading-relaxed ${big ? "max-w-md sm:text-base" : "max-w-xs"}`}>{s.short}</p>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const ServicesBento = () => (
  <section data-testid="services-bento" className="bg-porcelain py-24 sm:py-32">
    <div className="container-x">
      <div className="grid gap-8 lg:grid-cols-12 items-end mb-14">
        <div className="lg:col-span-8">
          <Reveal><p className="eyebrow text-cobalt mb-5">What we do</p></Reveal>
          <h2 className="font-wide font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            <MaskLines lines={["One crew.", "Every kind of job."]} />
          </h2>
        </div>
        <Reveal className="lg:col-span-4" delay={0.15}>
          <p className="text-slate-600 leading-relaxed">From the first pipe on a new build to looking after a whole strata complex — our team covers the full spectrum of plumbing.</p>
          <Link to="/services" data-testid="bento-all-services-link" className="mt-5 inline-flex items-center gap-2 font-bold text-cobalt link-underline">All services <ArrowUpRight className="h-4 w-4" /></Link>
        </Reveal>
      </div>
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[280px]">
        {LAYOUT.map((l, i) => <Tile key={l.slug} s={getService(l.slug)} cls={l.cls} big={l.big} i={i} />)}
      </div>
    </div>
  </section>
);
